import { ValidationError } from "../../types/error.ts";

/** Mirrors `@p0security/integrations-aws-backend`'s own `AwsPartition`/`arnInfo`
 * (`integrations-aws/src/shared/util.ts`), duplicated locally since `shared`
 * cannot import an integration package. Narrowed to partition-only
 * validation; does not validate `service` against AWS's allowlist. */
export const AwsPartitions = ["aws", "aws-us-gov"] as const;
export type AwsPartition = (typeof AwsPartitions)[number];

export const isAwsPartition = (value: unknown): value is AwsPartition =>
  AwsPartitions.includes(value as AwsPartition);

export type ArnInfo = {
  partition: AwsPartition;
  service: string;
  region?: string;
  accountId?: string;
  resourceType?: string;
  name?: string;
};

/**
 * The general format for an ARN looks like this:
 * arn:partition:service:region:account-id:resource-id
 * arn:partition:service:region:account-id:resource-type/resource-id
 * arn:partition:service:region:account-id:resource-type:resource-id
 *
 * See https://towardsthecloud.com/amazon-resource-names-arns
 *
 * Region and accountId are optional because some services don't require them. E.g. S3 ARN: arn:aws:s3:::p0-test-bucket-1
 */
export const arnInfo = (arn?: string): ArnInfo => {
  const parts = arn?.split(":") || [];
  const partition = parts[1];
  if (!isAwsPartition(partition)) {
    throw new ValidationError(
      "arn",
      { arn },
      `Partition ${partition} is not supported`
    );
  }
  return {
    partition,
    service: parts[2],
    region: parts[3] === "" ? undefined : parts[3],
    accountId: parts[4] === "" ? undefined : parts[4],
    resourceType:
      parts.length == 7
        ? parts[5] // arn:partition:service:region:account-id:resource-type:resource-id
        : parts[5]?.includes("/")
          ? parts[5]?.split("/")?.[0] // arn:partition:service:region:account-id:resource-type/resource-id
          : undefined, // arn:partition:service:region:account-id:resource-id
    name:
      parts.length == 7
        ? parts[6] // arn:partition:service:region:account-id:resource-type:resource-id
        : parts[5]?.includes("/")
          ? parts[5]?.split("/")?.[1] // arn:partition:service:region:account-id:resource-type/resource-id
          : parts[5] !== ""
            ? parts[5]
            : undefined, // arn:partition:service:region:account-id:resource-id
  };
};
